import { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setSelectedLocation } from '../../store/slices/calendarSlice';
import { fetchLocations } from '../../store/slices/locationsSlice';
import { LOCATIONS } from '../../utils/constants';
import LocationList from '../location-list/LocationList';

const LocationSwitcher = () => {
  const dispatch = useDispatch();
  const selectedLocation = useSelector((state) => state.calendar.selectedLocation);
  const { locations } = useSelector((state) => state.locations);
  const [showLocationList, setShowLocationList] = useState(false);
  const scrollContainerRef = useRef(null);

  // Fetch locations when component mounts
  useEffect(() => {
    dispatch(fetchLocations());
  }, [dispatch]);

  // Auto-scroll to selected location when it changes
  useEffect(() => {
    if (selectedLocation && scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const selectedButton = container.querySelector(`[data-location="${selectedLocation}"]`);
      
      if (selectedButton) {
        const containerRect = container.getBoundingClientRect();
        const buttonRect = selectedButton.getBoundingClientRect();
        
        // Check if button is outside visible area
        if (buttonRect.left < containerRect.left || buttonRect.right > containerRect.right) {
          // Scroll to center the selected button
          const scrollLeft = selectedButton.offsetLeft - (container.offsetWidth / 2) + (selectedButton.offsetWidth / 2);
          container.scrollTo({
            left: scrollLeft,
            behavior: 'smooth',
          });
        }
      }
    }
  }, [selectedLocation]);

  const handleLocationChange = (location) => {
    dispatch(setSelectedLocation(location));
  };

  // Get fallback locations from constants.js if database is empty
  const getFallbackLocations = () => {
    return LOCATIONS.map(name => ({ name, isActive: true, id: `fallback-${name}` }));
  };

  // Filter active locations
  // If database is empty, use fallback from constants.js
  let filteredLocations = [];
  if (locations.length === 0) {
    // Use fallback from constants.js if database is empty
    filteredLocations = getFallbackLocations();
  } else {
    filteredLocations = locations.filter((location) => location.isActive !== false);
  }

  // Get location names for display
  const locationNames = filteredLocations.map(loc => loc.name);

  return (
    <div className="w-full bg-gradient-to-b from-gray-50/30 to-white border-b border-gray-100">
      <div className="flex items-center px-5 py-4 sm:px-7 sm:py-5 gap-4">
        {/* Location tabs with horizontal scrolling - Segmented Control Style */}
        <div 
          ref={scrollContainerRef}
          className="flex-1 min-w-0 overflow-x-auto overflow-y-hidden"
          style={{
            scrollBehavior: 'smooth',
            WebkitOverflowScrolling: 'touch',
            overscrollBehaviorX: 'contain',
            overscrollBehaviorY: 'none',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }}
          onWheel={(e) => {
            const container = scrollContainerRef.current;
            if (container && container.scrollWidth > container.clientWidth) {
              if (e.shiftKey || Math.abs(e.deltaX) > Math.abs(e.deltaY)) {
                e.preventDefault();
                container.scrollBy({
                  left: e.deltaY || e.deltaX,
                  behavior: 'auto',
                });
              }
            }
          }}
        >
          <div 
            className="flex items-center gap-1 mx-auto py-1 px-1 bg-gray-100/80 rounded-2xl backdrop-blur-sm" 
            style={{ width: 'max-content', minWidth: 'fit-content' }}
          >
            {/* All Locations tab */}
            <button
              onClick={() => handleLocationChange(null)}
              data-location="__all__"
              className={`relative px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl text-sm sm:text-base font-semibold transition-all duration-300 ease-out whitespace-nowrap flex-shrink-0 ${
                selectedLocation === null
                  ? 'bg-white text-orange-600 shadow-lg shadow-orange-500/20 scale-105'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
              }`}
            >
              {selectedLocation === null && (
                <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/10 to-orange-600/10 animate-pulse" />
              )}
              <span className="relative">All Locations</span>
            </button>
            {/* Individual location tabs */}
            {locationNames.map((location, index) => (
              <button
                key={location}
                onClick={() => handleLocationChange(location)}
                data-location={location}
                className={`relative px-5 py-2.5 sm:px-6 sm:py-3 rounded-xl text-sm sm:text-base font-semibold transition-all duration-300 ease-out whitespace-nowrap flex-shrink-0 ${
                  selectedLocation === location
                    ? 'bg-white text-orange-600 shadow-lg shadow-orange-500/20 scale-105'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
                style={{
                  transitionDelay: `${index * 20}ms`,
                }}
              >
                {selectedLocation === location && (
                  <span className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/10 to-orange-600/10 animate-pulse" />
                )}
                <span className="relative">{location}</span>
              </button>
            ))}
          </div>
        </div>
        
        {/* Edit icon - Premium style */}
        <button
          onClick={() => setShowLocationList(!showLocationList)}
          className={`group relative p-2.5 rounded-xl transition-all duration-300 flex-shrink-0 ${
            showLocationList
              ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/30 scale-110'
              : 'bg-white text-gray-500 border border-gray-200 shadow-sm hover:bg-orange-50 hover:text-orange-600 hover:border-orange-200 hover:shadow-md hover:scale-110'
          }`}
          aria-label="Manage Locations"
          title="Manage Locations"
        >
          <svg 
            className={`w-5 h-5 transition-transform duration-300 ${showLocationList ? 'rotate-90' : 'group-hover:rotate-12'}`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        </button>
      </div>

      {/* Location List */}
      {showLocationList && (
        <div className="px-4 pb-4 sm:px-6 sm:pb-6 border-t border-gray-200">
          <LocationList isEditing={true} />
        </div>
      )}
    </div>
  );
};

export default LocationSwitcher;

